import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { isEmpty, get, isFunction, find } from 'lodash';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { browserHistory } from 'common/utils/browserHistory';
import {
  hasLimitedGenderOptions,
  cl787SexualityFields,
  scrn7ScreeningsCompletion,
} from 'common/utils/FeatureFlags/flags';
import { Button } from '@unite-us/app-components';
import Notifier from 'common/helpers/Notifier';
import { isHttpError } from 'common/utils/Error';
import { OverlaySpinner, Spinner } from 'common/spinners';
import findCurrentGroup from 'common/utils/findCurrentGroup';
import { getGroup } from 'common/utils/stateHelpers';
import { getEnumsFromState } from 'common/utils/Enums';
import {
  paymentsInsuranceInitialValues,
  validateInsuranceForParentForm,
} from 'src/components/Insurance/utils/';
import { isFocused, INSURANCE } from 'common/utils/isFocused';
import { fetchGroupContact, fetchProfileForms } from 'actions/Contact/Group';
import { fetchGroup } from 'actions/Group';
import { removeSelectedContact } from 'actions/Contact';
import { DOMAIN_CONSTANTS } from 'src/common/constants';
import enforcedConsentRoute from 'common/utils/Navigation/enforcedConsentRoute';
import { buildAccessibilityMessageFromErrors } from 'common/utils/Form';
import AddSupportingContactDetailsStep from 'pages/components/AddSupportingContactDetailsStep';
import Header from './Header';
import BasicInformation from './BasicInformation';
import ContactInformation from './ContactInformation';
import SupplementalInformation from './SupplementalInformation';
import ContactRequirementsForm from './ContactRequirementsForm';
import {
  COPY_CREATE_CLIENT,
  COPY_UPDATE_CLIENT,
  NOT_FOUND_HEADER_COPY,
  REGULAR_HEADER_COPY,
} from '../constants';
import {
  buildContactInitialValues,
  goToSearchStep,
  formatNewContactDataFromValues,
} from '../utils';

const getHeaderText = (existingContact) => (
  existingContact ? COPY_UPDATE_CLIENT : COPY_CREATE_CLIENT
);

const getSubHeaderText = (existingContact) => (
  existingContact ? REGULAR_HEADER_COPY : `${NOT_FOUND_HEADER_COPY} ${REGULAR_HEADER_COPY}`
);

const getSubmittingText = (existingContact) => (
  existingContact ? 'Updating Client...' : 'Creating Client...'
);

const buildInitialInsuranceValues = (insurances = []) => insurances
  .filter((insurance) => insurance.id !== '')
  .map((insurance) => paymentsInsuranceInitialValues(insurance));

export class AddContactInformationForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isFetching: false,
      isSubmitting: false,
      initialPaymentsIds: [],
      profileForms: [],
      showProfileForms: false,
      allLanguages: [],
      languagesLoaded: false,
      // Accessibility features
      submitFailed: false,
      errorCount: 0,
      accessibilityMessage: '',
      accessibilityMessageKey: 0,
    };

    // Form API reference for React Final Form
    this.formApi = null;

    // Cache for memoizing initial values
    this.cachedInitialValues = null;
    this.cachedContactId = null;
    this.cachedSelectedContactId = null;
    this.cachedContactDataId = null;
    this.cachedInsurances = null;

    this.onBackClick = this.onBackClick.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.finalizeStep = this.finalizeStep.bind(this);
    this.getInitialValues = this.getInitialValues.bind(this);
    this.renderProfileForms = this.renderProfileForms.bind(this);
    this.renderContactForm = this.renderContactForm.bind(this);
  }

  /* eslint-disable react/sort-comp */
  getInitialValues() {
    const {
      contact, contactData, selectedContact, insurances,
    } = this.props;
    const contactId = contact?.id || null;
    const selectedContactId = selectedContact?.id || null;
    const contactDataId = contactData?.id || null;

    if (this.hasCachedValues(contactId, selectedContactId, contactDataId, insurances)) {
      return this.cachedInitialValues;
    }

    this.cachedInitialValues = buildContactInitialValues({
      contact, selectedContact, contactData, insurances,
    });
    this.cachedContactId = contactId;
    this.cachedSelectedContactId = selectedContactId;
    this.cachedContactDataId = contactDataId;
    this.cachedInsurances = insurances;

    return this.cachedInitialValues;
  }

  hasCachedValues(contactId, selectedContactId, contactDataId, insurances) {
    return (
      this.cachedInitialValues &&
      this.cachedContactId === contactId &&
      this.cachedSelectedContactId === selectedContactId &&
      this.cachedContactDataId === contactDataId &&
      this.cachedInsurances === insurances
    );
  }

  renderSupplementalSection(submitFailed = false) {
    const {
      completeGroup,
      contactId,
      currentGroup,
    } = this.props;

    if (!completeGroup) {
      return <Spinner />;
    }

    return (
      <SupplementalInformation
        contactId={contactId}
        currentGroup={currentGroup}
        submitFailed={submitFailed}
      />
    );
  }

  renderProfileForms() {
    const existingContact = this.props.contactId || this.props.integrationContact;
    const header = getHeaderText(existingContact);

    return (
      <div>
        <AddSupportingContactDetailsStep
          activeStep={this.props.activeStep}
          profileForms={this.state.profileForms}
          contact={this.props.contact}
          groupId={this.props.groupId}
          contactId={this.props.contactId}
          header={header}
          checkForForms={this.props.checkForAttachedForms}
          clientRoute={this.props.isClientRoute}
          context={this.props.context}
        />
      </div>
    );
  }

  renderContactForm(allInitialValues) {
    const {
      completeGroup,
      contactId,
      enums,
      integrationContact,
      limitedGenderOptions,
      shouldShowSexualityFields,
    } = this.props;

    const {
      isSubmitting, submitFailed, errorCount, accessibilityMessage,
    } = this.state;
    const existingContact = contactId || integrationContact;
    const header = getHeaderText(existingContact);
    const subHeader = getSubHeaderText(existingContact);
    const submittingText = getSubmittingText(existingContact);
    const showErrorSummary = submitFailed && errorCount > 0;

    return (
      <div>
        <Header header={header} subHeader={subHeader} />
        <Form
          onSubmit={this.onSubmit}
          initialValues={allInitialValues}
          mutators={{ ...arrayMutators }}
          validate={(values) => {
            const errors = {};
            const shouldValidateInsurance = isFocused(
              this.props.currentGroup,
              INSURANCE,
            );
            const insuranceErrors = validateInsuranceForParentForm(
              values,
              shouldValidateInsurance,
            );
            return { ...errors, ...insuranceErrors };
          }}
        >
          {({ handleSubmit, form }) => {
            this.formApi = form;
            return (
              <form onSubmit={handleSubmit}>
                {showErrorSummary && this.renderErrorSummary(accessibilityMessage)}
                <OverlaySpinner text={submittingText} show={isSubmitting} />
                <OverlaySpinner text="Fetching Client..." show={this.state.isFetching} />
                <BasicInformation
                  enums={enums}
                  limitedGenderOptions={limitedGenderOptions}
                  shouldShowSexualityFields={shouldShowSexualityFields}
                  isExistingContact={!!contactId}
                />
                <ContactInformation form={form} />
                <ContactRequirementsForm
                  requireContactPreferences={completeGroup ? completeGroup.require_contact_preferences : false}
                  onLanguagesLoaded={(languages) => {
                    this.setState({ allLanguages: languages, languagesLoaded: true });
                  }}
                />
                {this.renderSupplementalSection(submitFailed)}
                <footer className="mt-6 text-right add-contact-information-form__footer">
                  <Button
                    id="go-back-btn"
                    onClick={this.onBackClick}
                    label="Go Back"
                    style={{ marginRight: '10px' }}
                    disabled={isSubmitting}
                  />
                  <Button
                    id="save-client-btn"
                    onClick={this.onSaveClientClick}
                    label={this.state.languagesLoaded ? 'Save Client' : 'Loading...'}
                    primary
                    disabled={isSubmitting || !this.state.languagesLoaded}
                  />
                </footer>
              </form>
            );
          }}
        </Form>
      </div>
    );
  }
  /* eslint-enable react/sort-comp */

  componentDidMount() {
    const {
      contactId,
      completeGroup,
      groupId,
      insurances,
      selectedContact,
      checkForAttachedForms,
    } = this.props;

    const initialIds = buildInitialInsuranceValues(insurances);

    this.setInitialPaymentsIds(initialIds);

    if (contactId && isEmpty(selectedContact)) {
      this.setState({ isFetching: true });
      this.props.fetchGroupContact(groupId, contactId).then(() => {
        this.setState({ isFetching: false });
      });
    }

    if (!completeGroup) {
      this.props.fetchGroup(this.props.groupId);
    }

    if (checkForAttachedForms) {
      this.fetchProfileForms();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    this.handleInsuranceChanges(prevProps);
    this.handleAccessibilityMessageUpdates(prevState);
  }

  handleInsuranceChanges(prevProps) { // eslint-disable-line react/sort-comp
    if (this.props.insurances !== prevProps.insurances) {
      const currentInitialIds = buildInitialInsuranceValues(this.props.insurances);

      this.setInitialPaymentsIds(currentInitialIds);
    }
  }

  handleSubmitFailedChanged() {
    this.updateAccessibilityMessage(true, 200, 50);
  }

  handleSubmitFailedCleared() {
    this.setState({ errorCount: 0, accessibilityMessage: '', accessibilityMessageKey: 0 });
  }

  handleMessageClearedButErrorsRemain() {
    this.updateAccessibilityMessage(false, 200, 300);
  }

  handleResubmittingWithErrors() {
    this.updateAccessibilityMessage(true, 300, 300);
  }

  handleAccessibilityMessageUpdates(prevState) {
    if (this.checkSubmitFailedChanged(prevState)) {
      this.handleSubmitFailedChanged();
      return;
    }

    if (this.checkSubmitFailedCleared(prevState)) {
      this.handleSubmitFailedCleared();
      return;
    }

    if (this.checkMessageClearedButErrorsRemain(prevState)) {
      this.handleMessageClearedButErrorsRemain();
      return;
    }

    if (this.checkResubmittingWithErrors(prevState)) {
      this.handleResubmittingWithErrors();
    }
  }

  onBackClick() {
    const { context, activeStep } = this.props;

    if (isFunction(activeStep.onBackClick)) {
      return activeStep.onBackClick();
    }
    return goToSearchStep({ context });
  }

  validateLanguagesLoaded() {
    return this.state.languagesLoaded;
  }

  onSaveClientClick = (event) => {
    event.preventDefault();
    this.setState({
      accessibilityMessage: '',
      accessibilityMessageKey: this.state.accessibilityMessageKey + 1,
    });

    if (!this.formApi) return;

    // Submit the form via React Final Form
    this.formApi.submit();

    // If validation failed, update accessibility state
    const { hasValidationErrors } = this.formApi.getState();
    if (hasValidationErrors) {
      this.setState({ submitFailed: true });
    }
  }

  handlePostSubmitNavigation(payload) {
    if (!payload) { return; }

    const { checkForAttachedForms, contactId, context } = this.props;
    const { profileForms } = this.state;

    if (checkForAttachedForms && profileForms.length) {
      this.setState({ showProfileForms: true });
    } else if (context.includes(DOMAIN_CONSTANTS.REFERRAL) && contactId != null) {
      browserHistory.push({
        pathname: '/referrals/create/add-resources',
        search: `?person=${contactId}`,
        state: { source: 'client' },
      });
    } else if (!checkForAttachedForms || !profileForms.length) {
      this.finalizeStep(payload);
    } else {
      this.setState({ showProfileForms: true });
    }
  }

  onSubmit(values = {}) {
    const { createOrUpdateContact } = this.props;

    this.setState({ isSubmitting: true, submitFailed: false });

    if (!this.validateLanguagesLoaded()) {
      this.setState({ isSubmitting: false, submitFailed: true });
      Notifier.dispatch(400, 'Languages are still loading. Please wait a moment and try again.');
      return Promise.resolve(null);
    }

    return createOrUpdateContact(
      formatNewContactDataFromValues(values),
      values.insurance,
      this.state.initialPaymentsIds,
      this.state.allLanguages,
    ).then((payload) => {
      this.setState({ isSubmitting: false }, () => {
        this.handlePostSubmitNavigation(payload);
      });
    }).catch(() => {
      this.setState({ isSubmitting: false, submitFailed: true });
    });
  }

  setInitialPaymentsIds(initialIds) {
    this.setState({
      initialPaymentsIds: initialIds,
    });
  }

  getFormErrorCount() {
    if (!this.formApi) return 0;
    const registeredFields = this.formApi.getRegisteredFields();
    let count = 0;
    registeredFields.forEach((fieldName) => {
      const fieldState = this.formApi.getFieldState(fieldName);
      if (fieldState && fieldState.error) count += 1;
    });
    return count;
  }

  updateAccessibilityMessage = (clearFirst = false, initialDelay = 200, buildDelay = 50) => {
    buildAccessibilityMessageFromErrors({
      getErrorCount: () => this.getFormErrorCount(),
      setState: (updates, callback) => this.setState(updates, callback),
      clearFirst,
      initialDelay,
      buildDelay,
    });
  }

  checkSubmitFailedChanged(prevState) {
    return !prevState.submitFailed && this.state.submitFailed;
  }

  checkSubmitFailedCleared(prevState) {
    return prevState.submitFailed && !this.state.submitFailed;
  }

  checkMessageClearedButErrorsRemain(prevState) {
    return (
      this.state.submitFailed &&
      !this.state.accessibilityMessage &&
      prevState.accessibilityMessage
    );
  }

  checkResubmittingWithErrors(prevState) {
    return (
      !prevState.isSubmitting &&
      this.state.isSubmitting &&
      this.state.submitFailed
    );
  }

  async fetchProfileForms() {
    const { data: { data } } = await this.props.fetchProfileForms(this.props.groupId);

    if (!data.length) return;

    this.setState({
      profileForms: data,
    });
  }

  finalizeStep(payload) {
    if (!isHttpError(payload)) {
      const contact = get(payload, 'data.data', {});
      const {
        activeStep,
        contactId,
        isClientRoute,
        context,
        scrn7ScreeningsCompletionEnabled,
      } = this.props;

      if (
        scrn7ScreeningsCompletionEnabled &&
        context.includes(DOMAIN_CONSTANTS.SCREENING)
      ) {
        const screeningPath = `/facesheet/${contact?.id}/screenings`;
        enforcedConsentRoute({
          contact,
          itemType: DOMAIN_CONSTANTS.SCREENING,
          to: screeningPath,
        });
      } else if (!isClientRoute && activeStep.onStepComplete) {
        activeStep.onStepComplete({
          contact,
          contactId,
          eventTracker: this.context.eventTracker,
          removeSelectedContact: this.props.removeSelectedContact,
        });
      } else {
        enforcedConsentRoute({
          contact,
          itemType: DOMAIN_CONSTANTS.CONTACT,
          to: `/facesheet/${contact.id}`,
        });
      }
    }
  }

  renderErrorSummary(accessibilityMessage) {
    const { errorCount } = this.state;

    return (
      <>
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4"
          role="alert"
        >
          <strong>
            Please fix {errorCount} {errorCount === 1 ? 'error' : 'errors'} before saving.
          </strong>
        </div>
        <div
          key={this.state.accessibilityMessageKey}
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          className="sr-only"
        >
          {accessibilityMessage}
        </div>
      </>
    );
  }

  render() {
    const allInitialValues = this.getInitialValues();

    return (
      <div className="add-contact-information-form">
        {this.state.showProfileForms ?
          this.renderProfileForms() :
          this.renderContactForm(allInitialValues)}
      </div>
    );
  }
}

AddContactInformationForm.propTypes = {
  activeStep: PropTypes.object.isRequired,
  completeGroup: PropTypes.object,
  contact: PropTypes.object.isRequired,
  contactData: PropTypes.object,
  contactId: PropTypes.string,
  context: PropTypes.string,
  createOrUpdateContact: PropTypes.func.isRequired,
  currentGroup: PropTypes.object.isRequired,
  enums: PropTypes.object.isRequired,
  fetchGroup: PropTypes.func.isRequired,
  fetchGroupContact: PropTypes.func.isRequired,
  groupId: PropTypes.string.isRequired,
  insurances: PropTypes.array,
  integrationContact: PropTypes.bool,
  removeSelectedContact: PropTypes.func.isRequired,
  selectedContact: PropTypes.object,
  fetchProfileForms: PropTypes.func.isRequired,
  checkForAttachedForms: PropTypes.bool.isRequired,
  isClientRoute: PropTypes.bool.isRequired,
  scrn7ScreeningsCompletionEnabled: PropTypes.bool,
  limitedGenderOptions: PropTypes.bool.isRequired,
  shouldShowSexualityFields: PropTypes.bool.isRequired,
};

AddContactInformationForm.defaultProps = {
  completeGroup: null,
  contactData: {},
  contactId: null,
  context: '',
  insurances: [],
  integrationContact: false,
  selectedContact: {},
  scrn7ScreeningsCompletionEnabled: false,
};

AddContactInformationForm.contextTypes = {
  eventTracker: PropTypes.func.isRequired,
};

function mapStateToProps(state, ownProps) {
  const {
    user,
    session,
    session: { groupId },
  } = state;

  const { contactId } = ownProps;

  const contacts = get(state, 'contacts.contacts', []);
  const searchedContacts = get(state, 'searchedContacts', []);
  const contact =
    find([...searchedContacts, ...contacts], { id: contactId }) || {};

  return {
    completeGroup: getGroup(state, groupId),
    contact,
    contactId,
    currentGroup: findCurrentGroup(user, session),
    enums: getEnumsFromState(state),
    groupId,
    scrn7ScreeningsCompletionEnabled: scrn7ScreeningsCompletion(state),
    limitedGenderOptions: hasLimitedGenderOptions(state),
    shouldShowSexualityFields: cl787SexualityFields(state),
  };
}

export default connect(
  mapStateToProps,
  {
    fetchGroupContact,
    fetchGroup,
    removeSelectedContact,
    fetchProfileForms,
  },
)(AddContactInformationForm);
